<template>
  <v-row no-gutters>
    <v-col cols="12">
      <v-row no-gutters>
        <dots-menu :items="dotsItems" @turnOnAddingByMap="turnOn('map')" @turnOnAddingByIds="turnOn('ids')">
          <template #activator="{ on }">
            <span id="relationMenuAttachWrapper" style="position: relative">
              <base-button
                v-on="getButtonListeners(on)"
                translation-path="dialog.assignObject"
                :button-style="buttonStyle"
                :loading="isDialogLoading"
              />
            </span>
          </template>
        </dots-menu>
        <multiple-relations-menu
          attach-id="relationMenuAttachWrapper"
          :is-visible.sync="isMultipleRelationsMenuVisible"
          :features="identifiedMapFeatures"
          @assignRelations="handleRelationAdding"
        />
      </v-row>
    </v-col>
    <main-input-dialog
      ref="new-relation-dialog"
      :is-visible.sync="computedIsDialogVisible"
      :loading="isDialogLoading"
      :title-translation="$i18n.t('dialog.assignObject')"
      agree-text="dialog.agreeAssign"
      dialog-width="30%"
      @agree="handleRelationAdding([newRelationalFeature])"
      :inputs="dialogInputs"
    >
      <template #dataSourceName.input="data">
        <v-col cols="6">
          <data-input
            v-model="newRelationalFeature['data_source_name']"
            v-bind="data"
            @change="updateAllowedFeatures"
          />
        </v-col>
      </template>
      <template #featureId.input="data">
        <v-col cols="6">
          <data-input v-model="newRelationalFeature['feature_id']" v-bind="data" />
        </v-col>
      </template>
    </main-input-dialog>
  </v-row>
</template>
<script>
import { call, get } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
export default {
  name: 'RelationsInput',
  components: {
    DataInput,
    DotsMenu,
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MultipleRelationsMenu: () => import('@/components/MultipleRelationsMenu'),
  },
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    dataSourceName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    identifiedMapFeatures: [],
    isMultipleRelationsMenuVisible: false,
    activeType: undefined,
    currentNewRelationDataSourceFeaturesIds: [],
    currentNewRelationDataSourceDescAttributeName: '',
    currentNewRelationDataSourceIdAttributeName: '',
    currentNewRelationDataSourceDescAttributeVerboseName: 'ID',
    dotsItems: [{ name: 'turnOnAddingByMap' }, { name: 'turnOnAddingByIds' }],
    featureIdLoading: false,
    isDialogLoading: false,
    newRelationalFeature: {},
  }),
  computed: {
    activeTool: get('tools/activeTool'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    dataSources: get('layers/metadata'),
    layers: get('layers/layers'),
    relations: get('relations/relations'),
    dataSourcesArray() {
      return this.$_objectToArray(this.dataSources);
    },
    buttonStyle() {
      return this.activeType
        ? {
            backgroundColor: '#e8f0fe !important',
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646d',
          };
    },
    dialogInputs() {
      return [
        {
          name: 'dataSourceName',
          translationPath: 'dialog.dataSource',
          dataType: {
            name: 'select',
          },
          items: this.dataSourcesArray,
          itemValue: 'name',
          itemText: 'verbose_name',
          loading: !this.mapPanelDataFetched,
          readonly: !this.mapPanelDataFetched,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
        {
          name: 'featureId',
          straightTitle: this.currentNewRelationDataSourceDescAttributeVerboseName,
          dataType: {
            name: 'select',
          },
          items: this.currentNewRelationDataSourceFeaturesIds,
          itemValue: 'id',
          itemText: 'desc',
          disabled: !this.newRelationalFeature['data_source_name'],
          loading: this.featureIdLoading,
          readonly: this.featureIdLoading,
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
        },
      ];
    },
    relationsList() {
      return this.relations?.[this.dataSourceName]?.[this.featureId] || [];
    },
    computedIsDialogVisible: {
      get() {
        return this.activeType === 'ids';
      },
      set() {
        this.activeType = undefined;
      },
    },
  },
  methods: {
    addRelation: call('relations/addRelation'),
    getDataSourceFeatures: call('layers/getDataSourceFeatures'),
    getButtonListeners(on) {
      return this.activeType
        ? {
            click: this.turnOff,
          }
        : on;
    },
    isNewObjectAlreadyAdded(dataSourceName, featureId) {
      return !!this.relationsList.find(
        relation => relation['data_source_name'] === dataSourceName && relation['feature_id'] == featureId
      );
    },
    async handleRelationAdding(features) {
      this.isDialogLoading = true;
      try {
        const payload = {
          data_source_name: this.dataSourceName,
          feature_id: this.featureId,
          features,
        };
        await this.addRelation(payload);
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        this.isDialogLoading = false;
        if (this.activeType === 'ids') {
          this.activeType = undefined;
        }
      } catch {
        this.isDialogLoading = false;
      }
    },
    turnOn(type) {
      this.activeType = type;
    },
    turnOff() {
      this.activeType = undefined;
      this.$root.$emit('identification-action', false);
    },
    async updateAllowedFeatures(dataSourceName) {
      const dataSourceAttributesSchema = this.dataSources[dataSourceName].attributes_schema;
      this.currentNewRelationDataSourceDescAttributeName =
        dataSourceAttributesSchema.desc_attribute_name || dataSourceAttributesSchema.id_name;
      this.currentNewRelationDataSourceIdAttributeName = dataSourceAttributesSchema.id_name;
      this.currentNewRelationDataSourceDescAttributeVerboseName =
        dataSourceAttributesSchema.attributes.find(
          attribute => attribute.name === this.currentNewRelationDataSourceDescAttributeName
        ).verbose_name || 'ID';
      delete this.newRelationalFeature['feature_id'];
      this.$refs['new-relation-dialog']?.$refs['form']?.resetValidation();
      this.featureIdLoading = true;
      try {
        const features = await this.getDataSourceFeatures({
          dataSource: dataSourceName,
          idsDescsOnly: true,
        });
        this.currentNewRelationDataSourceFeaturesIds = features;
        this.featureIdLoading = false;
      } catch {
        this.featureIdLoading = false;
      }
    },
  },
  created() {
    this.$root.$off('assignedObjects');
    this.$root.$on('assignedObjects', identifiedFeatures => {
      const mappedFeatures = identifiedFeatures.map(element => {
        return { features: element.features, dataSourceName: this.layers[element.id]?.data_source_name };
      });
      const featuresToAdd = [];
      for (const dataSource of mappedFeatures) {
        if (!dataSource.dataSourceName) {
          continue;
        }
        for (const feature of dataSource.features) {
          if (feature.id == this.featureId && feature.dataSource === this.dataSourceName) {
            continue;
          }
          if (!this.isNewObjectAlreadyAdded(feature.dataSource, feature.id)) {
            if (
              !featuresToAdd.find(
                feature => feature['data_source_name'] === feature.dataSource && feature['feature_id'] == feature.id
              )
            ) {
              featuresToAdd.push({
                data_source_name: feature.dataSource,
                feature_id: feature.id,
                layer_id: feature.layerId,
              });
            }
          }
        }
      }
      this.identifiedMapFeatures = featuresToAdd;
      if (this.identifiedMapFeatures.length === 1) {
        this.isMultipleRelationsMenuVisible = false;
        this.handleRelationAdding(this.identifiedMapFeatures);
      } else if (this.identifiedMapFeatures.length > 1) {
        this.isMultipleRelationsMenuVisible = true;
      } else {
        this.isMultipleRelationsMenuVisible = false;
      }
    });
  },
  watch: {
    activeType(nV) {
      if (nV === 'map') {
        this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
      }
    },
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'identification') {
        this.activeType = undefined;
      }
    },
    computedIsDialogVisible() {
      this.currentNewRelationDataSourceDescAttributeVerboseName = 'ID';
    },
    'newRelationalFeature.data_source_name'(nV) {
      if (!nV) {
        this.newRelationalFeature.feature_id = null;
      }
    },
    dataSources: {
      deep: true,
      handler(nV) {
        if (this.newRelationalFeature?.data_source_name && !nV[this.newRelationalFeature.data_source_name]) {
          this.newRelationalFeature.data_source_name = '';
        }
      },
    },
  },
  beforeDestroy() {
    this.turnOff();
  },
};
</script>
